import { FontStyles } from "../utils/font-styles.ts";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem } from "../components/base/form";
import { useState } from "react";
import { Button } from "../components/buttons/button.tsx";
import { TextInput } from "../components/inputs/text-input.tsx";
import { TrackEvent } from "../utils/analytics.ts";
import { postcodeSchema } from "./form/schema.ts";

const outerContainer = "flex flex-col lg:flex-row items-center gap-6.5 md:gap-8 lg:gap-[100px] self-stretch";

const headerContainer = "flex flex-col items-start self-stretch lg:self-center gap-3 md:gap-3.5 md:pb-3.5 lg:pb-0 grow";
const headerTitleStyle = `flex items-center g-2.5 self-stretch text-north-sea font-semibold ${FontStyles.h1}`;
const headerTextStyle = `flex items-center g-2.5 self-stretch text-slate leading-normal md:leading-normal ${FontStyles.h6}`;

const formContainer =
  "flex flex-col self-stretch md:self-center gap-6.5 p-7.5 bg-cloud border border-foggy-firth rounded-lg shadow-lg md:w-[500px] shrink-0";
const formHeaderContainer = "flex flex-col items-center self-stretch gap-3.5";
const formHeaderTitleStyle = `flex items-center g-2.5 self-stretch text-slate font-semibold leading-none md:leading-none ${FontStyles.h5}`;
const formHeaderTextStyle = `flex items-center g-2.5 self-stretch text-light-slate leading-none md:leading-none ${FontStyles["body-l"]}`;
const formInputContainer = "flex flex-col items-center self-stretch gap-3.5";

const LandingPageSchema = z.object({
  postcode: postcodeSchema,
});

export type FormFields = z.infer<typeof LandingPageSchema>;

export const HeaderAndForm = () => {
  const [loading, setLoading] = useState(false);

  const form = useForm<FormFields>({
    mode: "onTouched",
    resolver: zodResolver(LandingPageSchema),
    reValidateMode: "onChange",
  });

  const { handleSubmit } = form;

  const onSubmit = handleSubmit(
    (data, event) => {
      // this handler will be called only when the validation passes
      event?.preventDefault();
      setLoading(true);
      window.sessionStorage.setItem("valuationPostcode", data.postcode);
      TrackEvent({ name: "valuations_landing_page_form_submit", value: 20 });
      window.location.href = "/valuations";
    },
    (_, event) => {
      event?.preventDefault();
    },
  );

  return (
    <div className={outerContainer}>
      <div className={headerContainer}>
        <h1 className={headerTitleStyle}>Find out how much your property is worth</h1>
        <div className={headerTextStyle}>
          Get a free, no-obligation valuation of your home from ESPC, Edinburgh and East Central Scotland’s leading
          property experts.
        </div>
      </div>
      <Form {...form}>
        {/* https://github.com/orgs/react-hook-form/discussions/10855*/}
        {/* eslint-disable @typescript-eslint/no-misused-promises */}
        <form className={formContainer} onSubmit={onSubmit}>
          <div className={formHeaderContainer}>
            <div className={formHeaderTitleStyle}>Get your free property valuation</div>
            <div className={formHeaderTextStyle}>Enter your postcode below to get started</div>
          </div>

          <div className={formInputContainer}>
            <FormField
              control={form.control}
              name={"postcode"}
              render={() => (
                <FormItem className="w-full">
                  <FormControl>
                    <Controller
                      name={"postcode"}
                      control={form.control}
                      render={({ field: { onBlur, value, onChange } }) => (
                        <TextInput
                          state={form.formState.errors.postcode ? "error" : "default"}
                          id="postcode"
                          placeholder="Enter your postcode"
                          defaultValue={""}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          message={form.formState.errors.postcode?.message}
                        />
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <Button
            label="Continue"
            colorVariant="secondary"
            variant="solid"
            loading={loading}
            disabled={loading}
            type="submit"
          />
        </form>
      </Form>
    </div>
  );
};
